import {
  signInWithEmailAndPassword,
  signOut,
  User,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import type { FirebaseError } from 'firebase/app';
import { auth } from '../../config/firebase';
import { getAuthErrorMessage, getPasswordResetErrorMessage } from './authErrors';
import {
  GENERIC_LOGIN_FAILURE_MESSAGE,
  normalizeAuthEmail,
} from './authDiagnostics';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export const login = async (email: string, password: string) => {
  const normalizedEmail = normalizeAuthEmail(email);

  try {
    const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);

    return userCredential.user;
  } catch (error: unknown) {
    const code = (error as FirebaseError)?.code ?? '';

    if (
      code === 'auth/invalid-credential' ||
      code === 'auth/wrong-password' ||
      code === 'auth/user-not-found'
    ) {
      throw new Error(GENERIC_LOGIN_FAILURE_MESSAGE);
    }

    throw new Error(getAuthErrorMessage(error));
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    throw new Error(getAuthErrorMessage(error));
  }
};

export const register = async (email: string, password: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      normalizeAuthEmail(email),
      password,
    );

    return userCredential.user;
  } catch (error: unknown) {
    throw new Error(getAuthErrorMessage(error));
  }
};

export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export const resetPassword = async (email: string) => {
  const normalized = normalizeAuthEmail(email);
  if (!normalized) {
    throw new Error('Please enter a valid email address.');
  }

  try {
    await sendPasswordResetEmail(auth, normalized);
  } catch (error: unknown) {
    const message = getPasswordResetErrorMessage(error);
    if (message) {
      throw new Error(message);
    }

    // Enumeration-safe: swallow unknown errors (e.g. user-not-found).
  }
};
